import api from './api';

// Get all reports from backend
export const getReports = async (type = 'my') => {
    try {
        const response = await api.get('/expenses', { params: { type } });
        return response.data;
    } catch (error) {
        console.error('Error loading reports:', error);
        return []; // Fallback to empty
    }
};

// Save a report (Create or Update)
export const saveReport = async (report) => {
    try {
        if (report.id) {
            // Update
            const response = await api.put(`/expenses/${report.id}`, report);
            return response.data;
        } else {
            // Create
            const response = await api.post('/expenses', report);
            return response.data;
        }
    } catch (error) {
        console.error('Error saving report:', error);
        throw error;
    }
};

// Get a single report by ID
export const getReport = async (id) => {
    try {
        const response = await api.get(`/expenses/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching report:', error);
        return null;
    }
};

// Delete a report
export const deleteReport = async (id) => {
    try {
        await api.delete(`/expenses/${id}`);
        return true;
    } catch (error) {
        console.error('Error deleting report:', error);
        return false;
    }
};

// Add expense item to report
// Note: Backend might define a specific endpoint for items, or we update the whole report.
// Based on created API, we have PUT /expenses/:id which accepts items.
// So we can re-use saveReport logic or creating specific Item APIs later.
// For now, let's assume valid flow involves fetching, local update, and PUT.
// Add expense item to report
export const addExpenseItem = async (reportId, item, currentItems = null) => {
    let items = currentItems;
    let report = null;

    if (!items) {
        report = await getReport(reportId);
        if (!report) return null;
        items = report.items || [];
    }

    const newItem = { ...item };
    const updatedItems = [...items, newItem];

    if (report) {
        return saveReport({ ...report, items: updatedItems });
    } else {
        // We still need the full report to save it
        report = await getReport(reportId);
        return saveReport({ ...report, items: updatedItems });
    }
};

// Update expense item
export const updateExpenseItem = async (reportId, itemId, updates, currentItems = null) => {
    let items = currentItems;
    let report = null;

    if (!items) {
        report = await getReport(reportId);
        if (!report) return null;
        items = report.items || [];
    }

    const updatedItems = items.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
    );

    if (report) {
        return saveReport({ ...report, items: updatedItems });
    } else {
        report = await getReport(reportId);
        return saveReport({ ...report, items: updatedItems });
    }
};

// Remove expense item
export const removeExpenseItem = async (reportId, itemId, currentItems = null) => {
    let items = currentItems;
    let report = null;

    if (!items) {
        report = await getReport(reportId);
        if (!report) return null;
        items = report.items || [];
    }

    const updatedItems = items.filter(item => item.id !== itemId);

    if (report) {
        return saveReport({ ...report, items: updatedItems });
    } else {
        report = await getReport(reportId);
        return saveReport({ ...report, items: updatedItems });
    }
};

// Calculate total (Client-side helper, though backend does it too)
const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
};

// Submit report for approval
export const submitReport = async (reportId) => {
    try {
        const response = await api.post(`/expenses/${reportId}/submit`);
        return response.data;
    } catch (error) {
        console.error('Error submitting report:', error);
        throw error;
    }
};

// Approve report
export const approveReport = async (reportId, comments = '') => {
    try {
        const response = await api.post(`/expenses/${reportId}/approve`, { comments });
        return response.data;
    } catch (error) {
        console.error('Error approving report:', error);
        throw error;
    }
};

// Reject report
export const rejectReport = async (reportId, comments = '') => {
    try {
        const response = await api.post(`/expenses/${reportId}/reject`, { comments });
        return response.data;
    } catch (error) {
        console.error('Error rejecting report:', error);
        throw error;
    }
};

// Get audit trail for report
export const getAuditTrail = async (reportId) => {
    try {
        const response = await api.get(`/expenses/${reportId}/audit`);
        return response.data;
    } catch (error) {
        console.error('Error fetching audit trail:', error);
        return [];
    }
};
